/**
 * Colorful weather glyphs for Ambient / Screen Saver.
 * Keys match weatherIconMap conditionIcon values. Do not use WeatherAPI CDN URLs.
 */
import React, { useId } from 'react';
import { Box } from '@mui/material';
import { resolveWeatherIconKey } from './weatherIconMap';

function SunnyGlyph() {
  const id = useId();
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <defs>
        <radialGradient id={`${id}-sun`} cx="50%" cy="42%" r="55%">
          <stop offset="0%" stopColor="#FFE56B" />
          <stop offset="100%" stopColor="#FF9F0A" />
        </radialGradient>
      </defs>
      <g transform="translate(32 32)" fill={`url(#${id}-sun)`}>
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <rect key={deg} x="-2.2" y="-29" width="4.4" height="9" rx="2.2" transform={`rotate(${deg})`} />
        ))}
        <circle r="13.5" />
      </g>
    </svg>
  );
}

function ClearNightGlyph() {
  const id = useId();
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <defs>
        <linearGradient id={`${id}-moon`} x1="20%" y1="0%" x2="80%" y2="100%">
          <stop offset="0%" stopColor="#F7F3E4" />
          <stop offset="100%" stopColor="#C5CDD8" />
        </linearGradient>
        <mask id={`${id}-crescent`} maskUnits="userSpaceOnUse">
          <rect width="64" height="64" fill="black" />
          <circle cx="30" cy="32" r="18" fill="white" />
          <circle cx="38" cy="26" r="14" fill="black" />
        </mask>
      </defs>
      <circle cx="30" cy="32" r="18" fill={`url(#${id}-moon)`} mask={`url(#${id}-crescent)`} />
      <circle cx="48" cy="16" r="1.3" fill="#F8FAFC" opacity="0.9" />
      <circle cx="52" cy="24" r="0.9" fill="#F8FAFC" opacity="0.7" />
    </svg>
  );
}

function CloudShape({ fill, opacity = 1 }) {
  return (
    <path
      d="M46.8 28.2c-.6-7.4-6.8-13.2-14.4-13.2-5.2 0-9.8 2.7-12.4 6.8-1.3-.5-2.7-.8-4.2-.8-6.3 0-11.4 5.1-11.4 11.4 0 .6 0 1.1.1 1.7C2.3 35.2.8 38.4.8 42c0 6 4.9 10.9 10.9 10.9h36.6c6.2 0 11.3-5 11.3-11.3 0-5.4-3.8-10-8.8-11.2Z"
      fill={fill}
      opacity={opacity}
    />
  );
}

function PartlyCloudyGlyph() {
  const id = useId();
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <defs>
        <radialGradient id={`${id}-sun`} cx="50%" cy="42%" r="55%">
          <stop offset="0%" stopColor="#FFE56B" />
          <stop offset="100%" stopColor="#FF9F0A" />
        </radialGradient>
      </defs>
      <g transform="translate(42 20)" fill={`url(#${id}-sun)`}>
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <rect key={deg} x="-1.6" y="-18" width="3.2" height="5.5" rx="1.6" transform={`rotate(${deg})`} />
        ))}
        <circle r="8.2" />
      </g>
      <g transform="translate(2 8) scale(0.92)">
        <CloudShape fill="#9AA6B4" opacity="0.35" />
        <g transform="translate(0 2)">
          <CloudShape fill="#F4F7FB" />
        </g>
      </g>
    </svg>
  );
}

function CloudyGlyph() {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <g transform="translate(4 8)">
        <CloudShape fill="#8B98A8" opacity="0.45" />
        <g transform="translate(1 3)">
          <CloudShape fill="#E8EEF5" />
        </g>
      </g>
    </svg>
  );
}

function RainGlyph() {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <g transform="translate(4 2) scale(0.92)">
        <CloudShape fill="#C5CED8" />
      </g>
      <path d="M20 48c0 3-2.2 5-4.2 5s-4.2-2-4.2-5 2.4-7.2 4.2-9.2c1.8 2 4.2 6.2 4.2 9.2Z" fill="#5AC8F5" />
      <path d="M32 51c0 3-2.2 5-4.2 5s-4.2-2-4.2-5 2.4-7.2 4.2-9.2c1.8 2 4.2 6.2 4.2 9.2Z" fill="#32ADE6" />
      <path d="M44 48c0 3-2.2 5-4.2 5s-4.2-2-4.2-5 2.4-7.2 4.2-9.2c1.8 2 4.2 6.2 4.2 9.2Z" fill="#5AC8F5" />
    </svg>
  );
}

function StormGlyph() {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <g transform="translate(3 1) scale(0.92)">
        <CloudShape fill="#6B7C90" />
      </g>
      <path d="M34 34 24.5 48h8.2L28 60l16.5-18.5h-8.8L41.5 34H34Z" fill="#FFD60A" />
    </svg>
  );
}

function SnowGlyph() {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <g transform="translate(4 2) scale(0.9)">
        <CloudShape fill="#E8EEF5" />
      </g>
      {[
        [20, 50],
        [32, 55],
        [44, 49],
      ].map(([x, y]) => (
        <g key={`${x}-${y}`} transform={`translate(${x} ${y})`} stroke="#F8FAFC" strokeWidth="1.4" strokeLinecap="round">
          <path d="M0-4.2v8.4M-4.2 0h8.4M-3-3 3 3M3-3-3 3" />
        </g>
      ))}
    </svg>
  );
}

function FogGlyph() {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <g fill="#D5DDE6" stroke="none">
        <rect x="8" y="18" width="40" height="5.5" rx="2.75" opacity="0.95" />
        <rect x="14" y="28" width="38" height="5.5" rx="2.75" opacity="0.8" />
        <rect x="6" y="38" width="42" height="5.5" rx="2.75" opacity="0.7" />
        <rect x="16" y="48" width="34" height="5.5" rx="2.75" opacity="0.55" />
      </g>
    </svg>
  );
}

function WindGlyph() {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <g fill="none" stroke="#D5DDE6" strokeWidth="3.2" strokeLinecap="round">
        <path d="M10 24h32c5 0 8-4 8-8s-3.5-7-8-7" />
        <path d="M10 34h38c4.5 0 8 3.2 8 7.2 0 4-3.2 7.3-8 7.3" />
        <path d="M10 44h22" />
      </g>
    </svg>
  );
}

function UnknownGlyph() {
  return (
    <svg viewBox="0 0 64 64" fill="none" aria-hidden>
      <circle cx="32" cy="32" r="22" fill="#3A4654" />
      <path
        d="M32 40.5v-1.2c0-3.2 2.1-5.1 4.4-6.8 2.1-1.6 3.6-3 3.6-5.4 0-3.1-2.5-5.2-6-5.2-3.6 0-6.1 2.2-6.4 5.6"
        stroke="#E2E8F0"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <circle cx="32" cy="46.5" r="2.2" fill="#E2E8F0" />
    </svg>
  );
}

const GLYPHS = Object.freeze({
  sunny: SunnyGlyph,
  'clear-night': ClearNightGlyph,
  'partly-cloudy': PartlyCloudyGlyph,
  cloudy: CloudyGlyph,
  rain: RainGlyph,
  storm: StormGlyph,
  snow: SnowGlyph,
  fog: FogGlyph,
  wind: WindGlyph,
  unknown: UnknownGlyph,
});

/**
 * @param {{
 *   conditionIcon?: string,
 *   sx?: object,
 * }} props
 */
export default function WeatherGlyph({ conditionIcon, sx, ...rest }) {
  const key = resolveWeatherIconKey(conditionIcon);
  const Glyph = GLYPHS[key] || GLYPHS.unknown;
  return (
    <Box
      component="span"
      data-weather-glyph={key}
      aria-hidden
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '1em',
        height: '1em',
        flexShrink: 0,
        lineHeight: 0,
        filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.28))',
        '& svg': { width: '100%', height: '100%', display: 'block', overflow: 'visible' },
        ...sx,
      }}
      {...rest}
    >
      <Glyph />
    </Box>
  );
}
